import { createFromProduct } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { addToChainAfter, addToChainBefore, addToEndOfChain, addToStartOfChain, replaceInChain } from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductState, getVariantChildrenDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { chain, LoadProductIfNeeded, LoadRealTimeInventory } from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/DisplayProduct";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { dispatchProductDetailEvent } from "@store/Services/AnalyticsService";
import { getCategory } from "@store/Store/Data/Categories/CategoriesSelectors";
import clearState from "@store/Store/Pages/ProductDetails/Parts/Handlers/ClearState";
import displayParts from "@store/Store/Pages/ProductDetails/Parts/Handlers/DisplayParts";
import clearStateWholegoods from "@store/Store/Pages/ProductDetails/Wholegoods/Handlers/ClearStateWholegoods";
import displayWholegoods from "@store/Store/Pages/ProductDetails/Wholegoods/Handlers/DisplayWholegoods";

addToStartOfChain(chain, props => {
	// `includeAttributes: ["includeOnProduct"]` needs to be sent to the "getProductByPath" service within the "LoadProductIfNeeded" handler chain.
	(props.parameter as any) = { includeAttributes: ["includeOnProduct"], ...props.parameter };
});

addToChainBefore(chain, LoadProductIfNeeded, props => {
    props.dispatch(
        clearState(),
    );
    props.dispatch(
        clearStateWholegoods(),
    );
});

addToChainAfter(chain, LoadProductIfNeeded, props => {
    if (props.product) {
        props.dispatch(
            displayParts({ productId: props.product.id }),
        );
        props.dispatch(
		    displayWholegoods({ productSKU: props.product.properties.fullErpNumber }),
        );
    }
});

function getVariantSelection(product?: ProductModel, variantChildren?: ProductModel[], styledOption?: string) {
    const variantSelection: SafeDictionary<string> = {};
    if (!product?.isVariantParent || !variantChildren) {
        return variantSelection;
    }

    if (styledOption) {
        const variantChild = variantChildren.find(o => o.productNumber.toLowerCase() === styledOption.toLowerCase());
        variantChild?.childTraitValues?.forEach(o => {
            variantSelection[o.styleTraitId] = o.id;
        });

        if (Object.keys(variantSelection).length > 0) {
            return variantSelection;
        }
    }

    product.variantTraits?.forEach(variantTrait => {
        const defaultVariantValue = variantTrait.traitValues?.find(traitValue => traitValue.isDefault);
        variantSelection[variantTrait.id] = defaultVariantValue?.id ?? "";
    });

    return variantSelection;
}

replaceInChain(chain, LoadRealTimeInventory, props => {
    if (!props.product || getIsWebCrawler()) {
        return;
    }

    props.dispatch(
        loadRealTimeInventory({
            productIds: [props.product.id, ...(props.variantChildren?.map(o => o.id) ?? [])],
            expand: ["warehouses"], // Poolcorp Custom Code
            onComplete: realTimeInventoryProps => {
                const realTimeInventory = realTimeInventoryProps?.apiResult;

                if (realTimeInventoryProps?.error) {
                    if (props.product) {
                        props.dispatch({
                            type: "Pages/ProductDetails/FailedLoadRealTimeInventory",
                            productId: props.product.id,
                        });
                    }
                } else if (realTimeInventory) {
                    if (realTimeInventory.realTimeInventoryResults && props.variantChildren && props.product) {
                        const discontinued: SafeDictionary<boolean> = {};
                        for (const result of realTimeInventory.realTimeInventoryResults) {
                            if (!result.qtyOnHand) {
                                discontinued[result.productId] = true;
                            }
                        }
                        const initialCount = props.variantChildren.length;
                        props.variantChildren = props.variantChildren.filter(
                            o => !o.isDiscontinued || (o.isDiscontinued && !discontinued[o.id]),
                        );

                        if (initialCount !== props.variantChildren.length) {
                            const variantChildrenDataView = getVariantChildrenDataView(
                                props.getState(),
                                props.product.id,
                            );
                            const { value, ...otherProps } = variantChildrenDataView;
                            props.dispatch({
                                type: "Data/Products/ReplaceDataView",
                                parameter: {
                                    productId: props.product.id,
                                    variantChildren: true,
                                },
                                dataView: {
                                    value: props.variantChildren,
                                    ...otherProps,
                                },
                            });

                            const productInfo =
                                props.getState().pages.productDetails.productInfosById?.[props.product.id] ??
                                createFromProduct(
                                    getProductState(props.getState(), props.product.id).value ?? props.product,
                                );

                            props.dispatch({
                                type: "Pages/ProductDetails/UpdateVariantSelection",
                                variantSelection: getVariantSelection(
                                    props.product,
                                    props.variantChildren,
                                    props.parameter.styledOption,
                                ),
                                variantSelectionCompleted: false,
                                selectedProductInfo: productInfo,
                            });
                        }
                    }

                    props.dispatch({
                        type: "Pages/ProductDetails/CompleteLoadRealTimeInventory",
                        realTimeInventory,
                    });
                }

                props.inventoryLoaded = true;
            },
        }),
    );
});

addToEndOfChain(chain, async props => {
    await waitFor(() => !!props.pricingLoaded);
    const state = props.getState();
    const { productInfosById } = state.pages.productDetails;
    const currentCurency = state.context.session.currency?.currencyCode;

    if (props.product && productInfosById) {
        const productInfo = productInfosById[props.product.id];
        await waitFor(() => !!props.pricingLoaded);

        const category = getCategory(props.getState());
        if (props.product && productInfosById && productInfo) {
            const productContext: ProductContextModel = {
                product: props.product,
                productInfo,
            };
            dispatchProductDetailEvent(productContext, category?.shortDescription || "", currentCurency);
        }
    }
});